import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Form, Input, Select, Space, Popconfirm, Card, Typography, Row, Col, Divider, Tabs, Statistic, List, Avatar, Alert, Progress, Tag, Timeline, Spin, App } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, SaveOutlined, CloseOutlined, EyeOutlined, ProjectOutlined, TeamOutlined, FileTextOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, ArrowLeftOutlined, BulbOutlined, LockOutlined, SearchOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAdminStore } from '../../store/useAdminStore';
import { useLocation } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

const EmployeeManagement = () => {
  const { message } = App.useApp();
  const location = useLocation();
  const { employees, loading, fetchEmployees, createEmployee, updateEmployee, deleteEmployee } = useAdminStore();
  const [currentView, setCurrentView] = useState('list'); // 'list', 'create', 'edit', 'detail'
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [form] = Form.useForm();
  
  // Search and filter states
  const [searchText, setSearchText] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Table state persistence
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [tableFilters, setTableFilters] = useState({});
  const [tableSorter, setTableSorter] = useState({});
  
  useEffect(() => {
    if (location?.state?.from === "dashboard") {
      setSelectedEmployee(null);
      form.resetFields();
      setCurrentView('create');
    }
  }, [location?.state]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Filtered and searched employee data
  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      // Search filter
      const searchLower = searchText.toLowerCase();
      const matchesSearch = !searchText || 
        employee.name?.toLowerCase().includes(searchLower) ||
        employee.email?.toLowerCase().includes(searchLower) ||
        employee.department?.toLowerCase().includes(searchLower) ||
        employee.position?.toLowerCase().includes(searchLower);
      
      // Department filter
      const matchesDepartment = filterDepartment === 'all' || employee.department === filterDepartment;
      
      // Role filter
      const matchesRole = filterRole === 'all' || employee.role === filterRole;
      
      // Status filter
      const matchesStatus = filterStatus === 'all' || 
        (filterStatus === 'active' && employee.isActive) ||
        (filterStatus === 'inactive' && !employee.isActive);
      
      return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
    });
  }, [employees, searchText, filterDepartment, filterRole, filterStatus]);

  // Get unique departments from employees
  const departments = useMemo(() => {
    const depts = [...new Set(employees.map(emp => emp.department).filter(Boolean))];
    return depts.sort();
  }, [employees]);

  const handleClearFilters = () => {
    setSearchText('');
    setFilterDepartment('all');
    setFilterRole('all');
    setFilterStatus('all');
    setTableFilters({});
  };

  const handleAdd = () => {
    setSelectedEmployee(null);
    form.resetFields();
    setCurrentView('create');
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    form.setFieldsValue({
      name: employee.name,
      email: employee.email,
      department: employee.department,
      position: employee.position,
      role: employee.role,
      isActive: employee.isActive
    });
    setCurrentView('edit');
  };

  const handleViewDetail = (employee) => {
    setSelectedEmployee(employee);
    setCurrentView('detail');
  };

  const handleCancel = () => {
    setCurrentView('list');
    setSelectedEmployee(null);
    form.resetFields();
  };

  const handleDelete = async (employeeId) => {
    const result = await deleteEmployee(employeeId);
    if (result.success) {
      message.success('Employee deleted successfully');
    } else {
      message.error(result.error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      let result;
      if (selectedEmployee) {
        result = await updateEmployee(selectedEmployee._id, values);
        message.success('Employee updated successfully');
      } else {
        result = await createEmployee(values);
        message.success('Employee created successfully');
      }
      
      if (result.success) {
        setCurrentView('list');
        setSelectedEmployee(null);
        form.resetFields();
      }
    } catch (error) {
      message.error('Operation failed');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      filters: [
        { text: 'Engineering', value: 'Engineering' },
        { text: 'Design', value: 'Design' },
        { text: 'Administration', value: 'Administration' },
      ],
      onFilter: (value, record) => record.department === value,
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          role === 'admin' ? 'bg-red-100 text-red-800' : 
          role === 'hr' ? 'bg-purple-100 text-purple-800' :
          role === 'project_manager' ? 'bg-indigo-100 text-indigo-800' : 
          'bg-blue-100 text-blue-800'
        }`}>
          {role === 'project_manager' ? 'Project Manager' : 
           role === 'hr' ? 'HR' : role}
        </span>
      ),
      filters: [
        { text: 'Admin', value: 'admin' },
        { text: 'HR', value: 'hr' },
        { text: 'Project Manager', value: 'project_manager' },
        { text: 'Employee', value: 'employee' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Employee"
            description="Are you sure you want to delete this employee?"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(record._id);
            }}
            onCancel={(e) => e?.stopPropagation()}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordForm] = Form.useForm();

  const handleResetPassword = async (values) => {
    setResetPasswordLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/reset-password-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedEmployee._id,
          newPassword: values.newPassword,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        message.success('Password reset successfully');
        resetPasswordForm.resetFields();
        setShowResetPassword(false);
      } else {
        message.error(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      message.error('Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const renderEmployeeForm = () => (
    <Row gutter={[24, 24]}>
      {/* Left Column - Employee Information */}
      <Col xs={24} lg={12}>
        <Card title="Employee Information" className="h-full">
          <div className="mb-4 flex justify-end">
            <Space>
              <Button icon={<CloseOutlined />} onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={() => form.submit()} loading={loading}>
                {selectedEmployee ? 'Update' : 'Create'} Employee
              </Button>
            </Space>
          </div>
          
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="name"
              label="Full Name"
              rules={[{ required: true, message: 'Please enter employee name' }]}
            >
              <Input placeholder="Enter full name" size="large" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: 'Please enter email address' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="Enter email address" size="large" />
            </Form.Item>

            {!selectedEmployee && (
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Please enter password' }]}
              >
                <Input.Password placeholder="Enter password" size="large" />
              </Form.Item>
            )}

            <Form.Item
              name="department"
              label="Department"
              rules={[{ required: true, message: 'Please select department' }]}
            >
              <Select placeholder="Select department" size="large">
                <Option value="Engineering">Engineering</Option>
                <Option value="Design">Design</Option>
                <Option value="Marketing">Marketing</Option>
                <Option value="Sales">Sales</Option>
                <Option value="HR">Human Resources</Option>
                <Option value="Finance">Finance</Option>
                <Option value="Management">Management</Option>
                <Option value="PMO">PMO</Option>
                <Option value="QA Testing">QA Testing</Option>
                <Option value="Cloud & Infra">Cloud & Infra</Option>
                <Option value="Administration">Administration</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="position"
              label="Position"
              rules={[{ required: true, message: 'Please enter position' }]}
            >
              <Input placeholder="Enter position/job title" size="large" />
            </Form.Item>

            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Please select role' }]}
              initialValue="employee"
            >
              <Select placeholder="Select role" size="large">
                <Option value="employee">Employee</Option>
                <Option value="project_manager">Project Manager</Option>
                <Option value="hr">HR</Option>
                <Option value="admin">Admin</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="isActive"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
              initialValue={true}
            >
              <Select placeholder="Select status" size="large">
                <Option value={true}>Active</Option>
                <Option value={false}>Inactive</Option>
              </Select>
            </Form.Item>
          </Form>
        </Card>
      </Col>

      {/* Right Column - Additional Options */}
      <Col xs={24} lg={12}>
        <Card title="Additional Options" className="h-full">
          {selectedEmployee ? (
            <div>
              <div className="mb-6">
                <Title level={5}>Employee Details</Title>
                <div className="space-y-2">
                  <div><Text strong>Employee ID:</Text> {selectedEmployee._id}</div>
                  <div><Text strong>Created:</Text> {dayjs(selectedEmployee.createdAt).format('MMM DD, YYYY')}</div>
                  <div><Text strong>Last Updated:</Text> {dayjs(selectedEmployee.updatedAt).format('MMM DD, YYYY')}</div>
                </div>
              </div>

              <Divider />

              <div className="mb-6">
                <Title level={5}>Password Management</Title>
                <Text type="secondary" className="block mb-4">
                  Reset the employee's password. They will need to use the new password to log in.
                </Text>
                
                {!showResetPassword ? (
                  <Button 
                    type="default" 
                    icon={<LockOutlined />}
                    onClick={() => setShowResetPassword(true)}
                    size="large"
                  >
                    Reset Password
                  </Button>
                ) : (
                  <div>
                    <Form
                      form={resetPasswordForm}
                      layout="vertical"
                      onFinish={handleResetPassword}
                    >
                      <Form.Item
                        name="newPassword"
                        label="New Password"
                        rules={[
                          { required: true, message: 'Please enter a new password' },
                          { min: 6, message: 'Password must be at least 6 characters long' }
                        ]}
                      >
                        <Input.Password placeholder="Enter new password" size="large" />
                      </Form.Item>

                      <Form.Item
                        name="confirmPassword"
                        label="Confirm New Password"
                        dependencies={['newPassword']}
                        rules={[
                          { required: true, message: 'Please confirm the new password' },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('newPassword') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('Passwords do not match'));
                            },
                          }),
                        ]}
                      >
                        <Input.Password placeholder="Confirm new password" size="large" />
                      </Form.Item>

                      <Space>
                        <Button 
                          onClick={() => {
                            setShowResetPassword(false);
                            resetPasswordForm.resetFields();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          type="primary" 
                          htmlType="submit" 
                          loading={resetPasswordLoading}
                          icon={<LockOutlined />}
                        >
                          Reset Password
                        </Button>
                      </Space>
                    </Form>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <UserOutlined className="text-4xl text-gray-300 mb-4" />
              <Title level={4} type="secondary">
                Creating New Employee
              </Title>
              <Text type="secondary">
                Fill in the employee information on the left to create a new employee account.
              </Text>
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );

  const EmployeeDetailContainer = () => {
    const [timesheetData, setTimesheetData] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(true);

    return (
      <Row gutter={[24, 0]} className="h-full">
        {/* Main Content - 70% */}
        <Col span={17}>
          <EmployeeDetailView setTimesheetData={setTimesheetData} setActivitiesLoading={setActivitiesLoading} />
        </Col>
        
        {/* Activities Sidebar - 30% */}
        <Col span={7}>
          <EmployeeActivitiesSidebar timesheetData={timesheetData} loading={activitiesLoading} />
        </Col>
      </Row>
    );
  };

  const renderEmployeeDetail = () => <EmployeeDetailContainer />;

  const EmployeeDetailView = ({ setTimesheetData, setActivitiesLoading }) => {
    const [employeeStats, setEmployeeStats] = useState({
      totalProjects: 0,
      activeProjects: 0,
      totalHours: 0,
      totalTimesheets: 0,
      approvedTimesheets: 0,
      pendingTimesheets: 0,
      rejectedTimesheets: 0,
      utilizationRate: 0,
      avgHoursPerDay: 0,
      projectAllocations: []
    });
    const [localTimesheetData, setLocalTimesheetData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      if (selectedEmployee?._id) {
        fetchEmployeeStats();
      }
    }, [selectedEmployee]);

    const fetchEmployeeStats = async () => {
      try {
        setLoading(true);
        setActivitiesLoading(true);
        
        // Fetch employee timesheets and project data
        const [timesheetsRes, projectsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/timesheets`),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects`)
        ]);

        const timesheetsData = await timesheetsRes.json();
        const projectsData = await projectsRes.json();

        if (timesheetsData.success && projectsData.success) {
          const allTimesheets = timesheetsData.data || [];
          const projects = projectsData.data || [];
          
          // Filter timesheets for this specific employee
          const timesheets = allTimesheets.filter(ts => 
            ts.employeeId === selectedEmployee._id || 
            ts.employeeId?._id === selectedEmployee._id
          );
          
          // Calculate employee project allocations
          const employeeProjects = projects.filter(project => {
            const isTeamMember = project.teamMembers?.some(member => 
              member.employeeId === selectedEmployee._id || 
              member.employeeId?._id === selectedEmployee._id
            );
            const isProjectManager = project.projectManager === selectedEmployee._id || 
              project.projectManager?._id === selectedEmployee._id;
            const isTechManager = project.techManager === selectedEmployee._id || 
              project.techManager?._id === selectedEmployee._id;
            
            return isTeamMember || isProjectManager || isTechManager;
          });

          const projectAllocations = employeeProjects.map(project => {
            const member = project.teamMembers?.find(m => 
              m.employeeId === selectedEmployee._id || 
              m.employeeId?._id === selectedEmployee._id
            );
            const projectTimesheets = timesheets.filter(ts => 
              ts.projectId?._id === project._id || 
              ts.projectId === project._id
            );
            const projectHours = projectTimesheets.reduce((sum, ts) => sum + (ts.hours || 0), 0);
            
            const isProjectManager = project.projectManager === selectedEmployee._id || 
              project.projectManager?._id === selectedEmployee._id;
            const isTechManager = project.techManager === selectedEmployee._id || 
              project.techManager?._id === selectedEmployee._id;
            
            return {
              projectId: project._id,
              projectName: project.name,
              allocation: member?.allocation || (isProjectManager || isTechManager ? 100 : 0),
              hoursLogged: projectHours,
              timesheetCount: projectTimesheets.length,
              role: isProjectManager ? 'Project Manager' :
                    isTechManager ? 'Tech Manager' : 'Team Member'
            };
          });

          const totalHours = timesheets.reduce((sum, ts) => sum + (ts.hours || 0), 0);
          const approvedCount = timesheets.filter(ts => ts.status === 'approved').length;
          const pendingCount = timesheets.filter(ts => ts.status === 'submitted' || ts.status === 'pending').length;
          const rejectedCount = timesheets.filter(ts => ts.status === 'rejected').length;

          setEmployeeStats({
            totalProjects: employeeProjects.length,
            activeProjects: employeeProjects.filter(p => p.status === 'active').length,
            totalHours,
            totalTimesheets: timesheets.length,
            approvedTimesheets: approvedCount,
            pendingTimesheets: pendingCount,
            rejectedTimesheets: rejectedCount,
            utilizationRate: timesheets.length > 0 ? Math.round((approvedCount / timesheets.length) * 100) : 0,
            avgHoursPerDay: timesheets.length > 0 ? Math.round((totalHours / timesheets.length) * 10) / 10 : 0,
            projectAllocations
          });

          // Sort timesheets by date (newest first)
          const sortedTimesheets = timesheets.sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
          
          setLocalTimesheetData(sortedTimesheets);
          setTimesheetData(sortedTimesheets);
        }
      } catch (error) {
        console.error('Error fetching employee stats:', error);
      } finally {
        setLoading(false);
        setActivitiesLoading(false);
      }
    };

    const tabItems = [
      {
        key: 'overview',
        label: (
          <span>
            <UserOutlined />
            Overview
          </span>
        ),
        children: (
          <Card>
            <Row gutter={[16, 16]} className="mb-6">
              <Col xs={24} md={12}>
                <div className="mb-4">
                  <Typography.Text strong>Full Name:</Typography.Text>
                  <div className="mt-1">{selectedEmployee?.name}</div>
                </div>
                <div className="mb-4">
                  <Typography.Text strong>Email:</Typography.Text>
                  <div className="mt-1">{selectedEmployee?.email}</div>
                </div>
                <div className="mb-4">
                  <Typography.Text strong>Department:</Typography.Text>
                  <div className="mt-1">{selectedEmployee?.department}</div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="mb-4">
                  <Typography.Text strong>Position:</Typography.Text>
                  <div className="mt-1">{selectedEmployee?.position}</div>
                </div>
                <div className="mb-4">
                  <Typography.Text strong>Role:</Typography.Text>
                  <div className="mt-1">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      selectedEmployee?.role === 'admin' ? 'bg-red-100 text-red-800' : 
                      selectedEmployee?.role === 'hr' ? 'bg-purple-100 text-purple-800' :
                      selectedEmployee?.role === 'project_manager' ? 'bg-indigo-100 text-indigo-800' : 
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {selectedEmployee?.role === 'project_manager' ? 'Project Manager' : 
                       selectedEmployee?.role === 'hr' ? 'HR' : 
                       selectedEmployee?.role === 'admin' ? 'Admin' :
                       selectedEmployee?.role === 'employee' ? 'Employee' : selectedEmployee?.role}
                    </span>
                  </div>
                </div>
                <div className="mb-4">
                  <Typography.Text strong>Status:</Typography.Text>
                  <div className="mt-1">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      selectedEmployee?.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedEmployee?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Performance Statistics */}
            <Divider orientation="left">Performance Overview</Divider>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Total Projects"
                    value={employeeStats.totalProjects}
                    prefix={<ProjectOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Total Hours"
                    value={employeeStats.totalHours}
                    suffix="hrs"
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Approval Rate"
                    value={employeeStats.utilizationRate}
                    suffix="%"
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Avg Hours/Day"
                    value={employeeStats.avgHoursPerDay}
                    suffix="hrs"
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        ),
      },
      {
        key: 'allocations',
        label: (
          <span>
            <TeamOutlined />
            Project Allocations
          </span>
        ),
        children: (
          <Card loading={loading}>
            <Typography.Title level={4}>Project Allocations & Contributions</Typography.Title>
            {employeeStats.projectAllocations.length > 0 ? (
              <List
                dataSource={Array.isArray(employeeStats.projectAllocations) ? employeeStats.projectAllocations : []}
                loading={loading}
                renderItem={(allocation) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<ProjectOutlined />} />}
                      title={
                        <div className="flex items-center gap-2">
                          <span>{allocation.projectName}</span>
                          <Tag color="blue">{allocation.role}</Tag>
                        </div>
                      }
                      description={
                        <div>
                          <div className="mt-2 flex gap-2">
                            <Tag color="green">{allocation.allocation}% Allocated</Tag>
                            <Tag color="orange">{allocation.hoursLogged} hours logged</Tag>
                            <Tag color="purple">{allocation.timesheetCount} timesheets</Tag>
                          </div>
                          <div className="mt-2">
                            <Progress 
                              percent={Math.min(100, (allocation.hoursLogged / (allocation.allocation * 40)) * 100)} 
                              size="small"
                              status={allocation.hoursLogged > (allocation.allocation * 40) ? 'exception' : 'active'}
                            />
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Alert message="No project allocations found" type="info" />
            )}
          </Card>
        ),
      },
      {
        key: 'timesheets',
        label: (
          <span>
            <FileTextOutlined />
            Timesheet Analysis
          </span>
        ),
        children: (
          <Card loading={loading}>
            <Row gutter={[16, 16]} className="mb-6">
              <Col xs={8}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Total Submitted"
                    value={employeeStats.totalTimesheets}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={8}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Approved"
                    value={employeeStats.approvedTimesheets}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={8}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Pending"
                    value={employeeStats.pendingTimesheets}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
            </Row>

            <Typography.Title level={5}>Recent Timesheet Submissions</Typography.Title>
            <Table
              dataSource={localTimesheetData.slice(0, 10)}
              rowKey="_id"
              size="small"
              pagination={false}
              loading={loading}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'date',
                  key: 'date',
                  render: (date) => dayjs(date).format('MMM DD, YYYY'),
                  width: 120,
                },
                {
                  title: 'Project',
                  dataIndex: ['projectId', 'name'],
                  key: 'project',
                  ellipsis: true,
                  render: (text, record) => record.projectId?.name || 'N/A',
                },
                {
                  title: 'Hours',
                  dataIndex: 'hours',
                  key: 'hours',
                  width: 80,
                  render: (hours) => `${hours || 0}h`,
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  width: 100,
                  render: (status) => (
                    <Tag color={
                      status === 'approved' ? 'green' :
                      status === 'rejected' ? 'red' : 'orange'
                    }>
                      {status?.charAt(0).toUpperCase() + status?.slice(1) || 'N/A'}
                    </Tag>
                  ),
                },
              ]}
            />
          </Card>
        ),
      },
    ];

    return (
      <div>
        <div className="mb-4">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            className="mb-4"
          >
            Back to Employees
          </Button>
          <div className="flex items-center gap-2">
            <UserOutlined className="text-2xl text-blue-500" />
            <Title level={2} className="mb-0">{selectedEmployee?.name}</Title>
            <Tag color={selectedEmployee?.isActive ? 'green' : 'red'} className="ml-2">
              {selectedEmployee?.isActive ? 'Active' : 'Inactive'}
            </Tag>
          </div>
        </div>
        <Tabs defaultActiveKey="overview" items={tabItems} />
      </div>
    );
  };

  const EmployeeActivitiesSidebar = ({ timesheetData, loading }) => (
    <Card title={
      <div className="flex items-center gap-2">
        <BulbOutlined />
        <span>Recent Activities</span>
      </div>
    } className="h-full">
      {loading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : timesheetData.length > 0 ? (
        <Timeline
          items={timesheetData.slice(0, 10).map(timesheet => ({
            dot: timesheet.status === 'approved' ? 
              <CheckCircleOutlined className="text-green-500" /> : 
              timesheet.status === 'rejected' ? 
              <CloseCircleOutlined className="text-red-500" /> :
              <ClockCircleOutlined className="text-orange-500" />,
            children: (
              <div>
                <div className="font-medium">Timesheet {timesheet.status}</div>
                <div className="text-sm text-gray-500">
                  {timesheet.projectId?.name} - {timesheet.hours}h
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {dayjs(timesheet.date).format('MMM DD, YYYY')}
                </div>
              </div>
            ),
          }))}
        />
      ) : (
        <Alert message="No recent activities" type="info" />
      )}
    </Card>
  );

  return (
    <div className="p-6">
      {currentView !== 'detail' && (
        <div className="mb-6">
          <Title level={2}>
            <UserOutlined className="mr-2" />
            {currentView === 'list' ? 'Employee Management' :
             currentView === 'create' ? 'Create New Employee' :
             currentView === 'edit' ? 'Edit Employee' :
             'Employee Details'}
          </Title>
        </div>
      )}

      {currentView === 'list' && (
        <Card>
          {/* Search and Filter Section */}
          <div className="mb-4">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Input
                  placeholder="Search by name, email, department, or position..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="large"
                  allowClear
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={4}>
                <Select
                  placeholder="Department"
                  value={filterDepartment}
                  onChange={setFilterDepartment}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="all">All Departments</Option>
                  {departments.map(dept => (
                    <Option key={dept} value={dept}>{dept}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4}>
                <Select
                  placeholder="Role"
                  value={filterRole}
                  onChange={setFilterRole}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="all">All Roles</Option>
                  <Option value="admin">Admin</Option>
                  <Option value="hr">HR</Option>
                  <Option value="project_manager">Project Manager</Option>
                  <Option value="employee">Employee</Option>
                </Select>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4}>
                <Select
                  placeholder="Status"
                  value={filterStatus}
                  onChange={setFilterStatus}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="all">All Status</Option>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Col>
              <Col xs={12} sm={12} md={6} lg={4}>
                <Space>
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClearFilters}
                    size="large"
                  >
                    Clear
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    size="large"
                  >
                    Add
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          {/* Results Summary */}
          <div className="mb-4">
            <Text type="secondary">
              Showing {filteredEmployees.length} of {employees.length} employees
              {(searchText || filterDepartment !== 'all' || filterRole !== 'all' || filterStatus !== 'all') && (
                <Tag color="blue" className="ml-2">
                  <FilterOutlined /> Filtered
                </Tag>
              )}
            </Text>
          </div>

          <Table
            columns={columns}
            dataSource={filteredEmployees}
            rowKey="_id"
            loading={loading}
            onRow={(record) => ({
              onClick: () => handleViewDetail(record),
              style: { cursor: 'pointer' },
              className: 'hover:bg-gray-50 transition-colors duration-200'
            })}
            pagination={{
              current: tablePagination.current,
              pageSize: tablePagination.pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Total ${total} employees`,
            }}
            onChange={(pagination, filters, sorter) => {
              setTablePagination({
                current: pagination.current,
                pageSize: pagination.pageSize,
              });
              setTableFilters(filters);
              setTableSorter(sorter);
            }}
            scroll={{ x: 800 }}
          />
        </Card>
      )}

      {(currentView === 'create' || currentView === 'edit') && renderEmployeeForm()}
      {currentView === 'detail' && renderEmployeeDetail()}
    </div>
  );
};

export default EmployeeManagement;
